import type { PanchangamConfigService } from "./panchangam-config.service";
import type { S3StaticUploadService } from "./s3-static-upload.service";
import { PANCHANGAM_FOLDERS } from "../config/folder-info-urls";
import type {
    JsonValue,
    PanchangamFileContext,
    PanchangamFolderFile,
    PanchangamFolderPayload,
    PanchangamFolderState,
    PanchangamHistoryEvent,
    PanchangamHistoryEventType,
} from "../types";
import { generateLooseSchema, validateWithSchema } from "../utils/schema";

const nowIso = (): string => new Date().toISOString();

const sanitizeSegment = (value: string): string =>
    value
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9._-]/g, "_");

const ensureJsonFileName = (value: string): string =>
    value.toLowerCase().endsWith(".json") ? value : `${value}.json`;

const getUserName = (email: string, userName?: string): string => {
    const trimmed = (userName ?? "").trim();
    if (trimmed) {
        return trimmed;
    }

    const localPart = email.split("@")[0]?.trim();
    return localPart || "unknown";
};

const normalizeMetaData = (value?: PanchangamFolderFile["meta_data"]): PanchangamFolderFile["meta_data"] => ({
    information: value?.information,
    previous_version: Array.isArray(value?.previous_version) ? [...(value?.previous_version ?? [])] : [],
    history: Array.isArray(value?.history) ? [...(value?.history ?? [])] : [],
});

const normalizeFolderPayload = (folderId: string, value?: Partial<PanchangamFolderPayload>): PanchangamFolderPayload => ({
    id: value?.id || folderId,
    files: Array.isArray(value?.files)
        ? (value?.files ?? [])
            .filter((entry): entry is PanchangamFolderFile => Boolean(entry?.id && entry?.static_url))
            .map((entry) => ({
                id: entry.id,
                static_url: entry.static_url,
                meta_data: normalizeMetaData(entry.meta_data),
            }))
        : [],
});

const extractVersionFromUrl = (url: string): number | null => {
    const match = url.match(/_v(\d+)\.json(?:\?|$)/i);
    if (!match) {
        return null;
    }

    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
};

const getLatestCreatedVersion = (file: PanchangamFolderFile): number =>
    file.meta_data.history.reduce<number>((maxVersion, event) => {
        if (event.created_version == null) {
            return maxVersion;
        }

        return Math.max(maxVersion, event.created_version);
    }, 0);

const getCurrentDeployedVersion = (file: PanchangamFolderFile): number | null => {
    let deployedVersion: number | null = null;

    file.meta_data.history.forEach((event) => {
        deployedVersion = event.deployed_version;
    });

    return deployedVersion;
};

const getVersionUrlForNumber = (file: PanchangamFolderFile, version: number): string | null => {
    const match = file.meta_data.previous_version.find(
        (url) => extractVersionFromUrl(url) === version,
    );

    if (match) {
        return match;
    }

    return null;
};

const getLatestVersionUrl = (file: PanchangamFolderFile): string | null => {
    const latestVersion = getLatestCreatedVersion(file);
    if (latestVersion <= 0) {
        return null;
    }

    return getVersionUrlForNumber(file, latestVersion) || file.meta_data.previous_version[file.meta_data.previous_version.length - 1] || null;
};

const appendHistoryEvent = (
    file: PanchangamFolderFile,
    eventType: PanchangamHistoryEventType,
    params: {
        email: string;
        userName?: string;
        createdVersion: number | null;
        deployedVersion: number | null;
        schemaUpdated: boolean;
    },
): PanchangamFolderFile => {
    const event: PanchangamHistoryEvent = {
        event_type: eventType,
        event_ts: nowIso(),
        created_version: params.createdVersion,
        deployed_version: params.deployedVersion,
        user_name: getUserName(params.email, params.userName),
        email: params.email,
        schema_updated: params.schemaUpdated,
    };

    return {
        ...file,
        meta_data: {
            ...file.meta_data,
            history: [...file.meta_data.history, event],
        },
    };
};

const replaceFileInPayload = (
    payload: PanchangamFolderPayload,
    fileId: string,
    updater: (file: PanchangamFolderFile) => PanchangamFolderFile,
): PanchangamFolderPayload => {
    const index = payload.files.findIndex((file) => file.id === fileId);
    if (index < 0) {
        throw new Error("File not found");
    }

    const nextFiles = [...payload.files];
    nextFiles[index] = updater(nextFiles[index]);

    return { ...payload, files: nextFiles };
};

const parseJsonFile = async (file: File): Promise<JsonValue> => {
    const text = await file.text();
    return JSON.parse(text) as JsonValue;
};

export interface PanchangamFileOpsService {
    getFolderConfigs: () => Array<{ id: string; staticInfoUrl: string }>;
    loadFolders: () => Promise<PanchangamFolderState[]>;
    loadFolder: (folderId: string) => Promise<PanchangamFolderState>;
    loadFileContext: (params: { folderId: string; fileId: string }) => Promise<PanchangamFileContext>;
    readJsonAtUrl: (url: string) => Promise<JsonValue>;
    addFile: (params: {
        folderId: string;
        fileName: string;
        file: File;
        authorEmail: string;
        userName?: string;
    }) => Promise<PanchangamFolderState>;
    createVersion: (params: {
        folderId: string;
        fileId: string;
        file: File;
        authorEmail: string;
        userName?: string;
        schemaUpdated: boolean;
    }) => Promise<PanchangamFolderState>;
    deployLatest: (params: {
        folderId: string;
        fileId: string;
        authorEmail: string;
        userName?: string;
    }) => Promise<PanchangamFolderState>;
    undeploy: (params: {
        folderId: string;
        fileId: string;
        authorEmail: string;
        userName?: string;
    }) => Promise<PanchangamFolderState>;
    deleteFile: (params: {
        folderId: string;
        fileId: string;
        authorEmail: string;
        userName?: string;
    }) => Promise<PanchangamFolderState>;
}

export const createPanchangamFileOpsService = (
    configService: PanchangamConfigService,
    s3Service: S3StaticUploadService,
): PanchangamFileOpsService => {
    const getFolderConfig = (folderId: string): { id: string; staticInfoUrl: string } => {
        const config = PANCHANGAM_FOLDERS.find((folder) => folder.id === folderId);
        if (!config) {
            throw new Error(`Unknown folder: ${folderId}`);
        }

        return config;
    };

    const saveFolderPayload = async (
        folder: { id: string; staticInfoUrl: string },
        payload: PanchangamFolderPayload,
    ): Promise<void> => {
        await configService.writeJsonToStaticUrl(folder.staticInfoUrl, payload);
    };

    const loadFolder = async (folderId: string): Promise<PanchangamFolderState> => {
        const folder = getFolderConfig(folderId);

        try {
            const payload = await configService.readJsonFromUrl<PanchangamFolderPayload>(folder.staticInfoUrl);
            return { folder, payload: normalizeFolderPayload(folder.id, payload) };
        } catch (_error) {
            // Folder-info not found on CDN (e.g. new environment, newly added folder).
            // Auto-create an empty folder-info so subsequent adds work immediately.
            const emptyPayload = normalizeFolderPayload(folder.id, { id: folder.id, files: [] });

            try {
                await saveFolderPayload(folder, emptyPayload);
            } catch {
                // Write failed (e.g. presign unavailable) — return empty payload in memory
                // so the UI still renders; next load will retry creation.
            }

            return { folder, payload: emptyPayload };
        }
    };

    return {
        getFolderConfigs: () => [...PANCHANGAM_FOLDERS],

        loadFolders: async () => Promise.all(PANCHANGAM_FOLDERS.map((folder) => loadFolder(folder.id))),

        loadFolder,

        loadFileContext: async ({ folderId, fileId }) => {
            const { folder, payload } = await loadFolder(folderId);
            const file = payload.files.find((entry) => entry.id === fileId);

            if (!file) {
                throw new Error("File not found");
            }

            const latestVersion = getLatestCreatedVersion(file);
            const deployedVersion = getCurrentDeployedVersion(file);
            const latestVersionUrl = getLatestVersionUrl(file);

            return {
                folder,
                payload,
                file,
                latestVersion,
                deployedVersion,
                latestVersionUrl,
            };
        },

        readJsonAtUrl: async (url) => configService.readJsonFromUrl<JsonValue>(url),

        addFile: async ({ folderId, fileName, file, authorEmail, userName }) => {
            const { folder, payload } = await loadFolder(folderId);
            const cleanId = fileName.trim();

            if (!cleanId) {
                throw new Error("File name cannot be empty");
            }

            const duplicate = payload.files.find((entry) => entry.id.toLowerCase() === cleanId.toLowerCase());
            if (duplicate) {
                throw new Error("A file with the same id already exists in this folder");
            }

            const seedJson = await parseJsonFile(file);
            const safeFolder = sanitizeSegment(folderId);
            const normalizedName = ensureJsonFileName(sanitizeSegment(cleanId));
            const stableKey = `${safeFolder}/${normalizedName}`;
            const versionKey = `${safeFolder}/versions/${normalizedName.replace(/\.json$/i, "")}_v1.json`;

            const versionUrl = await configService.writeJsonToKey(versionKey, seedJson);
            const stableUrl = await configService.writeJsonToKey(stableKey, seedJson);

            let createdFile: PanchangamFolderFile = {
                id: cleanId,
                static_url: stableUrl,
                meta_data: {
                    previous_version: [versionUrl],
                    history: [],
                },
            };

            createdFile = appendHistoryEvent(createdFile, "file_created", {
                email: authorEmail,
                userName,
                createdVersion: 1,
                deployedVersion: 1,
                schemaUpdated: true,
            });

            const nextPayload: PanchangamFolderPayload = {
                ...payload,
                files: [...payload.files, createdFile],
            };

            await saveFolderPayload(folder, nextPayload);
            return { folder, payload: nextPayload };
        },

        createVersion: async ({ folderId, fileId, file, authorEmail, userName, schemaUpdated }) => {
            const { folder, payload } = await loadFolder(folderId);
            const target = payload.files.find((entry) => entry.id === fileId);

            if (!target) {
                throw new Error("File not found");
            }

            const content = await parseJsonFile(file);
            const latestVersion = getLatestCreatedVersion(target);
            const latestVersionUrl = getLatestVersionUrl(target);
            const deployedVersion = getCurrentDeployedVersion(target);

            if (latestVersionUrl && !schemaUpdated) {
                const baseline = await configService.readJsonFromUrl<JsonValue>(latestVersionUrl);
                const schema = generateLooseSchema(baseline);
                const validation = validateWithSchema(schema, content);

                if (!validation.valid) {
                    const topErrors = validation.errors.slice(0, 3).join("; ");
                    throw new Error(`Schema validation failed: ${topErrors}`);
                }
            }

            const nextVersion = latestVersion + 1;
            const safeFolder = sanitizeSegment(folderId);
            const normalizedName = ensureJsonFileName(sanitizeSegment(fileId));
            const versionKey = `${safeFolder}/versions/${normalizedName.replace(/\.json$/i, "")}_v${nextVersion}.json`;
            const versionUrl = await configService.writeJsonToKey(versionKey, content);

            const nextPayload = replaceFileInPayload(payload, fileId, (entry) => {
                let updated: PanchangamFolderFile = {
                    ...entry,
                    meta_data: {
                        ...entry.meta_data,
                        previous_version: [...entry.meta_data.previous_version, versionUrl],
                    },
                };

                updated = appendHistoryEvent(updated, "version_created", {
                    email: authorEmail,
                    userName,
                    createdVersion: nextVersion,
                    deployedVersion,
                    schemaUpdated,
                });

                return updated;
            });

            await saveFolderPayload(folder, nextPayload);
            return { folder, payload: nextPayload };
        },

        deployLatest: async ({ folderId, fileId, authorEmail, userName }) => {
            const { folder, payload } = await loadFolder(folderId);
            const target = payload.files.find((entry) => entry.id === fileId);

            if (!target) {
                throw new Error("File not found");
            }

            const latestVersion = getLatestCreatedVersion(target);
            const latestVersionUrl = getLatestVersionUrl(target);

            if (!latestVersionUrl || latestVersion <= 0) {
                throw new Error("No version available to deploy");
            }

            await s3Service.overrideStaticWithDynamic(target.static_url, latestVersionUrl);

            const nextPayload = replaceFileInPayload(payload, fileId, (entry) =>
                appendHistoryEvent(entry, "deployed", {
                    email: authorEmail,
                    userName,
                    createdVersion: null,
                    deployedVersion: latestVersion,
                    schemaUpdated: false,
                }),
            );

            await saveFolderPayload(folder, nextPayload);
            return { folder, payload: nextPayload };
        },

        undeploy: async ({ folderId, fileId, authorEmail, userName }) => {
            const { folder, payload } = await loadFolder(folderId);
            const target = payload.files.find((entry) => entry.id === fileId);

            if (!target) {
                throw new Error("File not found");
            }

            await configService.writeJsonToStaticUrl(target.static_url, {});

            const nextPayload = replaceFileInPayload(payload, fileId, (entry) =>
                appendHistoryEvent(entry, "undeployed", {
                    email: authorEmail,
                    userName,
                    createdVersion: null,
                    deployedVersion: null,
                    schemaUpdated: false,
                }),
            );

            await saveFolderPayload(folder, nextPayload);
            return { folder, payload: nextPayload };
        },

        deleteFile: async ({ folderId, fileId }) => {
            const { folder, payload } = await loadFolder(folderId);
            const target = payload.files.find((entry) => entry.id === fileId);

            if (!target) {
                throw new Error("File not found");
            }

            if (getCurrentDeployedVersion(target) !== null) {
                throw new Error("Undeploy the file before deleting");
            }

            const nextPayload: PanchangamFolderPayload = {
                ...payload,
                files: payload.files.filter((entry) => entry.id !== fileId),
            };

            await saveFolderPayload(folder, nextPayload);
            return { folder, payload: nextPayload };
        },
    };
};
